import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/**
 * Output shape of the berserkr.corpus_scoreboard RPC. Mirrors the JSONB
 * blob the RPC builds in one round-trip (see migration
 * berserkr-app/040_corpus_scoreboard_rpc.sql).
 */
export interface CorpusScoreboard {
  evidence_id: string | null;
  computed_at: string;
  empty: boolean;
  reason?: string;
  window?: {
    start_date: string;   // YYYY-MM-DD
    end_date: string;     // YYYY-MM-DD
    total_days: number;
  };
  actors?: Array<{
    actor: string;
    messages: number;
    pct_of_corpus: number;
    active_days: number;
    active_pct: number;
  }>;
  gaps?: {
    n_gaps: number;
    median_days: number;
    mean_days: number;
    p90_days: number;
    max_days: number;
  };
  longest_silences?: Array<{
    silence_started: string;  // YYYY-MM-DD
    silence_ended: string;    // YYYY-MM-DD
    silent_days: number;
  }>;
}

/**
 * Fetch the corpus scoreboard for a case. Resolves to the first evidence
 * corpus bound to that case (v0 has one). Returns the typed JSONB blob.
 *
 * Refresh: 5 minutes is plenty — the metrics only change when a new
 * ingest lands or chunks get re-summarized, which is human-scale slow.
 */
export function useCorpusScoreboard(caseId: string | undefined) {
  return useQuery<CorpusScoreboard, Error>({
    queryKey: ["corpus-scoreboard", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!caseId) {
        return { evidence_id: null, empty: true, computed_at: "", reason: "no case selected" };
      }
      const { data, error } = await berserkr().rpc("corpus_scoreboard", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as CorpusScoreboard) ?? {
        evidence_id: null,
        empty: true,
        computed_at: "",
        reason: "rpc returned null",
      };
    },
  });
}
